import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { OrderSchemaType, ItemPath } from '../order.schema';
import { OrderItemType } from '../types';

interface useItemSubtotalCalculatorProps {
  itemPath: ItemPath;
  itemType: OrderItemType;
  quantity: number;
}

interface ItemSubtotalCalculatorResult {
  unitPrice: number;
  itemTaxes: number;
  itemSubtotal: number;
}

export const useItemSubtotalCalculator = ({ itemPath, itemType, quantity }: useItemSubtotalCalculatorProps): ItemSubtotalCalculatorResult => {
  const { watch, setValue } = useFormContext<OrderSchemaType>();

  const priceDetailsPath = `${itemPath}.priceDetails` as const;
  const offeredProductMaterialPath = `${itemPath}.offeredProductMaterial` as const;

  const [weight, pricePerKgFinal, unitPrice, discountPercent, itemSubtotal, itemTaxes] = watch([
    `${offeredProductMaterialPath}.weigth`,
    `${priceDetailsPath}.pricePerKgFinal`,
    `${priceDetailsPath}.unitPrice`,
    `${priceDetailsPath}.discount`,
    `${priceDetailsPath}.itemSubtotal`,
    `${priceDetailsPath}.itemTaxes`
  ]);

  useEffect(() => {
    const numQty = Number(quantity) || 0;
    const numWeight = parseFloat(String(weight) || '0');
    const numFinalPriceKg = pricePerKgFinal ?? 0;
    const numUnitPrice = unitPrice ?? 0;
    const numDiscount = discountPercent ?? 0;
    let calculatedSubtotal = 0;

    if (itemType === OrderItemType.MATERIAL) {
      if (numQty > 0) {
        if (numUnitPrice > 0) {
          calculatedSubtotal = numUnitPrice * numQty;
        } else if (numFinalPriceKg > 0 && numWeight > 0) {
          calculatedSubtotal = numFinalPriceKg * numWeight * numQty;
        }
      }
    } else if (itemType) {
      if (numQty > 0 && numUnitPrice > 0) {
        calculatedSubtotal = numUnitPrice * numQty;
      }
    }

    if (calculatedSubtotal > 0 && numDiscount > 0 && numDiscount <= 100) {
      calculatedSubtotal = calculatedSubtotal * (1 - numDiscount / 100);
    }

    const currentSubtotalState = watch(`${priceDetailsPath}.itemSubtotal`);

    if (currentSubtotalState !== calculatedSubtotal) {
      setValue(`${priceDetailsPath}.itemSubtotal`, parseFloat(calculatedSubtotal.toFixed(2)), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [itemType, quantity, weight, pricePerKgFinal, unitPrice, discountPercent, setValue, itemPath, priceDetailsPath, watch]);
  
  return {
    unitPrice: unitPrice ?? 0,
    itemTaxes: itemTaxes ?? 0,
    itemSubtotal: itemSubtotal ?? 0,
  };
};